import { useLoadScript } from "@react-google-maps/api";
import ToggleButtonComponent from "components/ToggleButton/ToggleButton";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";

import "../../../assets/css/style.css";
import crossWhite from "../../../assets/img/icons/remove_assignee.svg";
import CreateProjectDropdown from "../../../components/DropdownStyle/CreateProjectDropdown";
import ButtonTextLoader from "../../../components/Loaders/ButtonTextLoader";
import MapsAutocompleteDropdown from "../../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown";
import Select from "../../../components/ReactSelectDropdown";
import { filterOption } from "../../../components/ReactSelectDropdown/ReactSelectDropdown";
import { ClearSearchButton } from "../../../components/Search/searchStyles";
import useAutocompleteService from "../../../customHooks/usePlacesAutocompleteService";
import {
  ColoredButton,
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from "../../../styledComponents/buttons";
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from "../../../styledComponents/common";
import { checkHtml, checkUrl } from "../../../utils/helper";
import { timezoneToCoords } from "../../../utils/timeZoneCoordinates";
import {
  AddTaskLocationInfoContainer,
  AddTaskLocationSection,
} from "../../TasksNew/CreateTask/createTaskStyles";
import TaskLocationMap from "../../TasksNew/TaskLocationMap";
import HelperIcon from "components/HelperIcon/HelperIcon";

const mapLibraries = ["places"];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const AddLocationModal = ({
  isOpen,
  toggle,
  organization_id,
  client_id,
  addClientLocation,
  updateClientLocation,
  addOrUpdateclientLocalMapLoading,
  createdClientLocationInfo,
  clientLocationCreateUpdateSuccess,
  action,
  //updating the map
  id,
  name,
  address,
  radius,
  latitude,
  longitude,

  // autocomplete and geocoding service
  getGeocode,
  // addTaskLocationLoading,
  // createdTaskLocationInfo,
  // addTaskLocation,
  getTaskLocation,
  // getOfficesShortList,
  // officesShortList,
  defaultLocationStatus,
  selectedOrganization,
}) => {
  const { t } = useTranslation();
  const orgType = selectedOrganization?.type || "operation";
  const isOperation = orgType === "operation";
  
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [radiusInput, setRadiusInput] = useState(100);
  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [100, 150, 200, 300];
  const [isCreated, setIsCreated] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [addressMarker, setAddressMarker] = useState(null);
  const [defaultLocationSetStatus, setDefaultLocationSetStatus] = useState(
    action === "update" ? defaultLocationStatus : false,
  );
  // const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeOptions, setOfficeOptions] = useState([]);

  // map states
  const [map, setMap] = useState(null);

  // const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  const { isLoaded, loadError } = useLoadScript({
    id: "job-site-details-map-script-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 500 });

  // Calculate timezone-based map center
  const mapCenterFromTimezone = useMemo(() => {
    if (selectedOrganization && selectedOrganization.timezone) {
      return timezoneToCoords(selectedOrganization.timezone);
    }
    // Default to Makkah if no timezone available
    return { lat: 21.4225, lng: 39.8262 };
  }, [selectedOrganization?.timezone]);

  useEffect(() => {
    if (isOpen) {
      if (action === "update") {
        setLocationName(name || "");
        setLocationAddress(address || "");
        setRadiusInput(radius || 100);
        setDefaultLocationSetStatus(defaultLocationStatus || false);

        if (latitude && longitude) {
          setAddressMarker({
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          });
        }
      } else {
        setLocationName("");
        setLocationAddress("");
        setRadiusInput(100);
        setDefaultLocationSetStatus(false);
        setAddressMarker(null);
      }
      setSaveErrors({});
      setIsCreated(false);
      setIsUpdated(false);
    } else {
      clearState();
    }
  }, [
    isOpen,
    action,
    name,
    address,
    radius,
    latitude,
    longitude,
    defaultLocationStatus,
  ]);

  useEffect(() => {
    if (
      createdClientLocationInfo &&
      createdClientLocationInfo.id &&
      isOpen &&
      isCreated
    ) {
      handleToggle();
    }
  }, [createdClientLocationInfo]);

  useEffect(() => {
    if (clientLocationCreateUpdateSuccess && isOpen && isUpdated) {
      handleToggle();
    }
  }, [clientLocationCreateUpdateSuccess]);

  useEffect(() => {
    if (map && addressMarker) {
      handleMapPanAndZoomToSingleLocation(addressMarker);
    }
  }, [map, addressMarker]);

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setLocationAddress(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {});
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };

  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setLocationAddress(results.results[0].formatted_address);
          } else {
            setLocationAddress(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {});
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };

  const handleSetAddressMarker = location => {
    if (location) {
      setAddressMarker(location);
      // handleMapPanAndZoomToSingleLocation(location);
    }
  };

  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const onLocationNameChange = e => {
    const { value } = e.target;
    setLocationName(value);
    if (saveErrors && saveErrors["locationName"]) {
      delete saveErrors["locationName"];
    }
  };

  const onLocationAddressChange = e => {
    const { value } = e.target;
    getPlacePredictions({ input: value });
    setLocationAddress(value);
    if (saveErrors && saveErrors["locationAddress"]) {
      delete saveErrors["locationAddress"];
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;

    if (checkUrl(locationName)) {
      formIsValid = false;
      saveErrors["locationName"] = "task location name can not contain url";
    } else if (checkHtml(locationName)) {
      formIsValid = false;
      saveErrors["locationName"] = "Invalid job site name";
    }
    if (checkUrl(locationAddress)) {
      formIsValid = false;
      saveErrors["locationAddress"] = "Address can not contain url";
    } else if (checkHtml(locationAddress)) {
      formIsValid = false;
      saveErrors["locationAddress"] = "Invalid address input";
    }
    if (!radiusInput) {
      formIsValid = false;
      saveErrors["radius"] = "Radius is required";
    } else if (radiusInput % 1 !== 0) {
      formIsValid = false;
      saveErrors["radius"] = "Fraction is not allowed for radius";
    } else if (radiusInput < 50) {
      formIsValid = false;
      saveErrors["radius"] = "Minimum value for radius is 50";
    }
    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const handleAddTaskLocation = () => {
    if (checkError()) {
      const payload = {
        organizationID: organization_id,
        name: locationName,
        address: locationAddress,
        client: client_id,
        radius: radiusInput,
        default: defaultLocationSetStatus,
      };

      // if (selectedOffice && selectedOffice.value) {
      //   payload.office = selectedOffice.value;
      // }

      if (addressMarker && addressMarker.lat) {
        payload.latitude =
          typeof addressMarker.lat === "function"
            ? addressMarker.lat()
            : addressMarker.lat;
        payload.longitude =
          typeof addressMarker.lng === "function"
            ? addressMarker.lng()
            : addressMarker.lng;
      }

      if (action === "add") {
        addClientLocation(payload);
        setIsCreated(true);
      } else if (action === "update") {
        payload.id = id;
        updateClientLocation(payload);
        setIsUpdated(true);
      } else if (action == "create") {
        delete payload.organizationId;
        addClientLocation(payload);
        handleToggle();
      }
    }
  };

  const clearState = () => {
    setLocationName("");
    setLocationAddress("");
    setRadiusInput(100);
    setSaveErrors({});
    setAddressMarker(null);
    setMap(null);
    // setUserCurrentLocation(null);
    setIsCreated(false);
    setIsUpdated(false);
    setDefaultLocationSetStatus(false);
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `950px` }}
      className="custom-modal"
    >
      <ModalBody style={{ padding: `0` }}>
        <AddTaskLocationSection>
          <AddTaskLocationInfoContainer>
            <CommonFlex gap="4px" >
               <CommonText name title fontSize="16px" margin="auto 0 0px 0">
              {t(isOperation ? "add_task_location" : "add_job_location")}
            </CommonText>
            <HelperIcon
              text={t(
                "add_the_default_client_service_adress_or_any_additional_service_addresses_for_jobs_or_for_billing_purposes"
              )}
            />
          </CommonFlex>
           

            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t("location_name")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_location_name")}
                value={locationName}
                onChange={e => onLocationNameChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationName`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors["locationName"] &&
                saveErrors["locationName"] !== undefined && (
                  <FormAlert>{saveErrors["locationName"]}</FormAlert>
                )}
            </InputWithLabelSection>

            <InputWithLabelSection
              style={{ position: "relative" }}
              margin="20px 0 0"
            >
              <InputLabel>
                {t("location_address")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_location_address")}
                value={locationAddress}
                onChange={e => onLocationAddressChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationAddress`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors["locationAddress"] &&
                saveErrors["locationAddress"] !== undefined && (
                  <FormAlert>{saveErrors["locationAddress"]}</FormAlert>
                )}
              {!isPredictionsLoading &&
              placePredictions &&
              placePredictions.length &&
              locationAddress ? (
                <MapsAutocompleteDropdown
                  dropdownOptions={placePredictions}
                  handleSelect={handleGetGeocode}
                />
              ) : null}
            </InputWithLabelSection>

            <InputWithLabelSection margin="20px 0 0">
              <CommonGrid>
                <InputLabel>
                  {t("location_radius")} <RedStar>*</RedStar>
                </InputLabel>
              </CommonGrid>
              <CommonFlex justifyContent="space-between" gap="12px">
                {radiusOptions.map((option, i) => {
                  return (
                    <RadiusButton
                      key={option}
                      active={radiusInput === option}
                      onClick={() => {
                        if (radiusInput !== option) {
                          setRadiusInput(option);
                          if (saveErrors && saveErrors["radius"]) {
                            delete saveErrors["radius"];
                          }
                        }
                      }}
                    >
                      {option} m
                    </RadiusButton>
                  );
                })}
              </CommonFlex>
            </InputWithLabelSection>
            <InputWithLabelSection
              style={{ position: "relative" }}
              margin="20px 0 0"
            >
              <CommonFlex gap="12px">
                <CommonText>Set as default location</CommonText>
                <ToggleButtonComponent
                  active={defaultLocationSetStatus}
                  handleClick={() => setDefaultLocationSetStatus(prev => !prev)}
                />
              </CommonFlex>
            </InputWithLabelSection>

            <CommonGrid
              justifyContent="unset"
              padding="10px 0 0 0"
              margin="30px 0 0"
            >
              <WhiteButton onClick={() => handleToggle()}>
                {t("cancel")}
              </WhiteButton>
              <PrimaryButton
                disabled={!locationName || !locationAddress}
                onClick={() => handleAddTaskLocation()}
              >
                {addOrUpdateclientLocalMapLoading ? (
                  <ButtonTextLoader
                    loadingText={`${
                      action === "update" ? "Updating" : "Adding"
                    } `}
                    fontSize="13px"
                  />
                ) : (
                  `${action === "update" ? "Update" : "Add"} Location`
                )}
              </PrimaryButton>
            </CommonGrid>
          </AddTaskLocationInfoContainer>
          <TaskLocationMap
            setMap={setMap}
            singleAddressMarker={addressMarker}
            setAddressFromMap={handleSetAddressFromMap}
            radius={radiusInput}
            action={action}
            center={mapCenterFromTimezone}
          />
        </AddTaskLocationSection>
      </ModalBody>
    </Modal>
  );
};

export default AddLocationModal;
